import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import Modal from '../components/Modal.jsx';
import { useToast } from '../context/ToastContext.jsx';

/**
 * Traffic Controls -- QPS governance, operable rather than curl-able.
 *
 * Migration 069 replaced percentage sampling with token-bucket QPS caps, and until now the only
 * way to set one was a raw PATCH and the only way to read live traffic was a curl against
 * auction-service's OPS_API_KEY-guarded /ops/qps. A limit nobody can see is a limit nobody
 * trusts, and the measured cost of that was real: Cuedart at 70%, Admile and Pumpkin at 50%,
 * discarding live supply at a load average of 0.05, because the sampling percentages were set
 * once and never revisited.
 *
 * TWO SURFACES, DELIBERATELY NOT MERGED
 * -------------------------------------
 * Inbound (what BidDigi ACCEPTS from a supply seat) and outbound (what BidDigi SENDS a demand
 * partner) are different obligations with different consequences. Over-admitting inbound costs
 * BidDigi CPU. Over-sending outbound breaches a partner agreement, and partners respond to that
 * by silently dropping us rather than complaining. They share a mechanism and nothing else, so
 * they get separate tabs with separate language.
 *
 * WHAT THE LIVE NUMBERS ACTUALLY ARE
 * ----------------------------------
 * Telemetry is read from ONE auction-service process, and the box runs four. Each enforces its
 * own share of a cap (cap / worker_count) with its own buckets, so a reading here is that
 * process's traffic -- not the fleet's. The page says so, every time, rather than letting an
 * operator quietly divide by the wrong number. An unreachable sidecar renders the configured
 * caps with telemetry marked unavailable; it never blanks the controls, because the moment
 * telemetry breaks is usually the moment someone needs the dial.
 */

const REFRESH_MS = 15000;
const WINDOW_MINUTES = 60;

/** `—` for a genuinely absent figure. A missing measurement must never render as a zero. */
function num(v, suffix = '') {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString(undefined, { maximumFractionDigits: n < 10 ? 2 : 0 })}${suffix}`;
}

function capLabel(maxQps) {
  return maxQps === null || maxQps === undefined ? 'Uncapped' : `${maxQps.toLocaleString()} QPS`;
}

/** The one-line explanation of what is actually governing this row right now. */
function GovernanceBadge({ governedBy }) {
  const map = {
    qps: { cls: 'active', text: 'QPS cap' },
    sampling: { cls: 'paused', text: 'Sampling (legacy)' },
    uncapped: { cls: 'neutral', text: 'Uncapped' },
  };
  const m = map[governedBy] || map.uncapped;
  return <span className={`status-badge status-badge--${m.cls}`}>{m.text}</span>;
}

/** Live traffic cell. Renders "not measured" distinctly from "zero". */
function LiveCell({ live }) {
  if (!live) return <span className="cell-muted" title="Telemetry was not readable for this row. This is not a reading of zero.">not measured</span>;
  return (
    <>
      <div className="cell-title num">{num(live.qps60s)}</div>
      <div className="cell-subtitle">1s {num(live.qps1s)} · 10s {num(live.qps10s)}</div>
    </>
  );
}

export default function QpsPage() {
  const toast = useToast();
  const [tab, setTab] = useState('inbound');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // {kind:'seat'|'partner', row}
  const requestIdRef = useRef(0);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const id = ++requestIdRef.current;
    if (!quiet) setLoading(true);
    try {
      const result = await api.opsQps(WINDOW_MINUTES);
      // Stale-response guard, same as DemandPartnersPage: a slow first request must not land on
      // top of a fast refresh and show the operator older numbers than they already had.
      if (id !== requestIdRef.current) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (id !== requestIdRef.current) return;
      setError(err instanceof ApiError ? err.message : 'Could not load QPS governance.');
    } finally {
      if (id === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load({ quiet: true }), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const seats = data?.inboundSeats || [];
  const partners = data?.outboundPartners || [];
  const telemetry = data?.telemetry;

  return (
    <div className="page">
      <PageHeader
        title="Traffic Controls"
        subtitle="QPS limits on what BidDigi accepts from supply and sends to demand, with the traffic they are governing."
        actions={<button className="btn" onClick={() => load()}>Refresh</button>}
      />

      {telemetry && !telemetry.available && (
        <div className="form-error" role="status">
          <strong>Live traffic is unavailable.</strong> {telemetry.error}
        </div>
      )}
      {telemetry && telemetry.available && (
        <p className="card__hint">
          Live figures come from one auction-service process
          {telemetry.workerCount ? ` of ${telemetry.workerCount}` : ''}. Each process enforces its
          own share of a cap, so these are that process's QPS, not the fleet total. Recent
          outcomes cover the last {telemetry.windowMinutes ?? WINDOW_MINUTES} minutes. Refreshing
          every {REFRESH_MS / 1000}s.
        </p>
      )}

      <div className="button-row" role="tablist" style={{ marginBottom: '1rem' }}>
        <button
          role="tab"
          aria-selected={tab === 'inbound'}
          className={`btn${tab === 'inbound' ? ' btn--primary' : ''}`}
          onClick={() => setTab('inbound')}
        >
          Inbound supply ({seats.length})
        </button>
        <button
          role="tab"
          aria-selected={tab === 'outbound'}
          className={`btn${tab === 'outbound' ? ' btn--primary' : ''}`}
          onClick={() => setTab('outbound')}
        >
          Outbound demand ({partners.length})
        </button>
      </div>

      {loading && <LoadingState label="Loading QPS governance…" />}
      {!loading && error && <EmptyState title="Couldn't load traffic controls" message={error} />}

      {!loading && !error && tab === 'inbound' && (
        <section className="card card--flush">
          <div className="card__header card__header--padded">
            <h2 className="card__title">Inbound supply limits</h2>
            <span className="card__hint">
              What BidDigi will accept from each seat. Over-cap requests are refused with HTTP 429
              before any auction runs, so the cost of a rejection is near zero.
            </span>
          </div>
          {seats.length === 0
            ? <EmptyState title="No inbound seats" message="Create a seat before setting a rate limit on it." />
            : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Seat</th>
                      <th>Governed by</th>
                      <th className="num" title="Sustained requests per second this seat may send.">Max QPS</th>
                      <th className="num" title="Bucket depth: how far above the sustained rate a short spike may go before it is refused.">Burst</th>
                      <th className="num" title="Requests per second observed on one auction-service process, averaged over the last 60 seconds.">Observed</th>
                      <th className="num" title="Requests refused for being over the cap, in the last 60 seconds.">Throttled 60s</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {seats.map((s) => (
                      <tr key={s.seatId}>
                        <td>
                          <div className="cell-title">{s.name}</div>
                          <div className="cell-subtitle"><code className="code-chip">{s.seatId}</code> · {s.status}</div>
                        </td>
                        <td>
                          <GovernanceBadge governedBy={s.governedBy} />
                          {s.governedBy === 'sampling' && (
                            <div className="cell-subtitle">{s.sampleRatePct}% admitted</div>
                          )}
                        </td>
                        <td className="num">{capLabel(s.maxQps)}</td>
                        <td className="num">{s.burstQps === null ? '—' : s.burstQps.toLocaleString()}</td>
                        <td className="num"><LiveCell live={s.live} /></td>
                        <td className="num">
                          {s.live ? num(s.live.throttled60s) : <span className="cell-muted">—</span>}
                          {s.live?.sustainedOverload && (
                            <div className="cell-subtitle" style={{ color: 'var(--status-error-fg)' }}>sustained overload</div>
                          )}
                        </td>
                        <td className="num">
                          <button className="btn" onClick={() => setEditing({ kind: 'seat', row: s })}>Edit limit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </section>
      )}

      {!loading && !error && tab === 'outbound' && (
        <section className="card card--flush">
          <div className="card__header card__header--padded">
            <h2 className="card__title">Outbound demand limits</h2>
            <span className="card__hint">
              What BidDigi sends each partner. A partner over its bucket is skipped for that
              auction and recorded as <code className="code-chip">qps_capped</code> — never as a
              no-bid, so throttling by us is always separable from silence from them.
            </span>
          </div>
          {partners.length === 0
            ? <EmptyState title="No demand partners" message="Add a demand partner before setting a rate limit on it." />
            : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Partner</th>
                      <th className="num" title="Sustained requests per second BidDigi currently sends. An operational dial.">Max QPS</th>
                      <th className="num" title="What the partner contractually agreed to receive. A commercial fact, changed only on renegotiation.">Agreed</th>
                      <th className="num">Observed</th>
                      <th className="num" title="Calls skipped because the partner was over its cap, in the recent window.">QPS-capped</th>
                      <th className="num" title="Median and 95th-percentile partner response latency in the recent window.">Latency p50 / p95</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {partners.map((p) => (
                      <tr key={p.code}>
                        <td>
                          <div className="cell-title">{p.name}</div>
                          <div className="cell-subtitle">
                            <code className="code-chip">{p.code}</code> · {p.enabled ? 'enabled' : 'disabled'}
                          </div>
                        </td>
                        <td className="num">{capLabel(p.maxQps)}</td>
                        <td className="num">
                          {p.agreedMaxQps === null
                            ? <span className="cell-muted" title="No contracted QPS is recorded for this partner. Nothing is enforced against it -- record one to make the ceiling real.">none on file</span>
                            : (
                              <>
                                <div>{p.agreedMaxQps.toLocaleString()}</div>
                                {p.agreedQpsSource && <div className="cell-subtitle">{p.agreedQpsSource}</div>}
                              </>
                            )}
                        </td>
                        <td className="num"><LiveCell live={p.live} /></td>
                        <td className="num">
                          {p.recent ? num(p.recent.qpsCapped) : <span className="cell-muted">not measured</span>}
                          {p.recent && p.recent.calls > 0 && p.recent.qpsCapped > 0 && (
                            <div className="cell-subtitle">
                              {Math.round((100 * p.recent.qpsCapped) / p.recent.calls)}% of calls
                            </div>
                          )}
                        </td>
                        <td className="num">
                          {p.recent
                            ? `${num(p.recent.p50LatencyMs)} / ${num(p.recent.p95LatencyMs)} ms`
                            : <span className="cell-muted">—</span>}
                          <div className="cell-subtitle">timeout at {p.timeoutMs ?? '—'} ms</div>
                        </td>
                        <td className="num">
                          <button className="btn" onClick={() => setEditing({ kind: 'partner', row: p })}>Edit limit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </section>
      )}

      {editing && (
        <EditLimitModal
          kind={editing.kind}
          row={editing.row}
          onClose={() => setEditing(null)}
          onSaved={(message) => { setEditing(null); toast.success(message); load({ quiet: true }); }}
        />
      )}
    </div>
  );
}

/**
 * One modal for both surfaces, because the mechanism is identical and two near-copies would
 * drift. The language around it is not identical, and that is where `kind` is used.
 *
 * Shows the existing value beside the proposed one before saving: a QPS change is cheap to make
 * and expensive to get wrong, and "200 → 2000" read back in words is the difference between a
 * typo caught and a partner lost.
 */
function EditLimitModal({ kind, row, onClose, onSaved }) {
  const isSeat = kind === 'seat';
  const [maxQps, setMaxQps] = useState(row.maxQps === null ? '' : String(row.maxQps));
  const [burstQps, setBurstQps] = useState(row.burstQps === null ? '' : String(row.burstQps));
  const [agreed, setAgreed] = useState(row.agreedMaxQps === null || row.agreedMaxQps === undefined ? '' : String(row.agreedMaxQps));
  const [agreedSource, setAgreedSource] = useState(row.agreedQpsSource || '');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // '' means "uncapped" and is sent as an explicit null -- the API treats null as "remove the
  // cap", which is the only way back from a cap once set.
  const parse = (s) => (s.trim() === '' ? null : Number(s));
  const nextMax = parse(maxQps);
  const nextBurst = parse(burstQps);
  const nextAgreed = parse(agreed);

  const describe = (v) => (v === null ? 'uncapped' : `${v} QPS`);
  const changed = nextMax !== row.maxQps
    || nextBurst !== row.burstQps
    || (!isSeat && (nextAgreed !== (row.agreedMaxQps ?? null) || agreedSource !== (row.agreedQpsSource || '')));

  // The client-side mirror of the server's rules. The server is the boundary that matters -- this
  // exists so the common mistakes are named in the form rather than bounced back as a 400.
  function clientError() {
    for (const [label, v] of [['Max QPS', nextMax], ['Burst', nextBurst], ['Agreed QPS', nextAgreed]]) {
      if (v !== null && (!Number.isInteger(v) || v <= 0)) return `${label} must be a whole number above zero, or blank to remove it.`;
    }
    if (nextBurst !== null && nextMax === null) return 'A burst with no sustained rate has no meaning. Set Max QPS, or clear the burst.';
    if (nextBurst !== null && nextMax !== null && nextBurst < nextMax) return 'Burst must be greater than or equal to Max QPS, or the bucket cannot hold one second of its own refill and the effective rate falls below the cap.';
    if (!isSeat && nextAgreed !== null && nextMax !== null && nextMax > nextAgreed) {
      return `Max QPS ${nextMax} is above the ${nextAgreed} QPS this partner agreed to receive. Raise the agreed figure here too — and record where it comes from — or lower Max QPS.`;
    }
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = clientError();
    if (err) { setFormError(err); return; }
    setSaving(true);
    setFormError(null);
    try {
      if (isSeat) {
        await api.patchInboundSeat(row.seatId, { maxQps: nextMax, burstQps: nextBurst });
      } else {
        // snake_case: the demand-partner PATCH takes real column names, unlike the seat route.
        const body = { max_qps: nextMax, burst_qps: nextBurst };
        if (nextAgreed !== (row.agreedMaxQps ?? null)) body.agreed_max_qps = nextAgreed;
        if (agreedSource !== (row.agreedQpsSource || '')) body.agreed_qps_source = agreedSource.trim() || null;
        await api.patchDemandPartner(row.code, body);
      }
      onSaved(`${isSeat ? row.name : row.name} is now ${describe(nextMax)}.`);
    } catch (e2) {
      // The server's 409 for an over-contract cap carries `blockers`; showing them is the whole
      // value of that refusal.
      const body = e2 instanceof ApiError ? e2.body : null;
      const blockers = Array.isArray(body?.blockers) ? ` ${body.blockers.join(' ')}` : '';
      const hint = body?.hint ? ` ${body.hint}` : '';
      setFormError(`${e2 instanceof ApiError ? e2.message : 'Could not save the limit.'}${blockers}${hint}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={`${isSeat ? 'Inbound limit' : 'Outbound limit'} — ${row.name}`}
      onClose={onClose}
      width={560}
      footer={null}
    >
      <form onSubmit={handleSubmit} className="form">
        <p className="card__hint">
          {isSeat
            ? 'Sustained requests per second BidDigi will accept from this seat. Anything above it is refused with HTTP 429 before an auction runs. Leave blank to accept everything.'
            : 'Sustained requests per second BidDigi will send this partner. Calls above it are skipped and recorded as qps_capped. Leave blank to send without a limit.'}
        </p>

        <label className="field">
          <span className="field__label">Max QPS (sustained)</span>
          <input type="number" min="1" step="1" value={maxQps} placeholder="blank = uncapped"
            onChange={(e) => setMaxQps(e.target.value)} autoFocus />
        </label>

        <label className="field">
          <span className="field__label">Burst (bucket depth)</span>
          <input type="number" min="1" step="1" value={burstQps} placeholder="blank = same as Max QPS"
            onChange={(e) => setBurstQps(e.target.value)} />
        </label>
        <p className="card__hint">
          Burst is how far a short spike may run above the sustained rate before it is refused.
          Blank means the bucket is exactly one second deep. It must be at least Max QPS.
        </p>

        {!isSeat && (
          <>
            <label className="field">
              <span className="field__label">Agreed QPS (contractual)</span>
              <input type="number" min="1" step="1" value={agreed} placeholder="blank = no agreement on file"
                onChange={(e) => setAgreed(e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Where that figure comes from</span>
              <input type="text" value={agreedSource} placeholder='e.g. "IO 2026-09-14 clause 4" or "email from ops, 30 Sep"'
                onChange={(e) => setAgreedSource(e.target.value)} />
            </label>
            <p className="card__hint">
              Max QPS cannot be saved above the agreed figure. Raising the agreed figure is
              recording a renegotiation, not adjusting a dial — so it asks for a source, and the
              change is written to the audit log either way.
            </p>
          </>
        )}

        {changed && (
          <div className="card" style={{ padding: '0.75rem 1rem' }}>
            <div className="field__label">Before → after</div>
            <div>Max QPS: <strong>{describe(row.maxQps)}</strong> → <strong>{describe(nextMax)}</strong></div>
            <div>Burst: <strong>{describe(row.burstQps)}</strong> → <strong>{describe(nextBurst)}</strong></div>
            {!isSeat && (
              <div>Agreed: <strong>{describe(row.agreedMaxQps ?? null)}</strong> → <strong>{describe(nextAgreed)}</strong></div>
            )}
          </div>
        )}

        {formError && <div className="form-error">{formError}</div>}

        <div className="form__actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={saving || !changed}>
            {saving ? 'Saving…' : 'Save limit'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
